import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridDateComparator, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QuoteLineItemGridColumnGroup,
  QuoteLineItemGridField,
  QuoteLineItemGridRow,
} from '../../NegotiationQuoteDetailsWidget.types';
import { SupplierRelationshipType } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  dateFormatterFactory,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  NegotiationCaseQuoteStatusCell,
  numberFormatterFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { CommodityLinkCell } from '~/components/DataGridCustom/cells/CommodityLinkCell/CommodityLinkCell';
import { CuriosityLocationCell } from '~/components/DataGridCustom/cells/CuriosityLocationCell/CuriosityLocationCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { IconId } from '~/components/Icon';
import { RouteId } from '~/routes';

const relationshipColorMap: { [key in SupplierRelationshipType]?: string } = {
  [SupplierRelationshipType.known]: 'success.main',
  [SupplierRelationshipType.partner]: 'secondary.main',
};
export const statusColorFromRelationship = (value: SupplierRelationshipType): string | undefined => {
  return value ? relationshipColorMap[value] : undefined;
};

export const useQuoteDataGridConfig: UseDataGridConfig<QuoteLineItemGridRow> = () => {
  const { t } = useTranslation();
  const { features } = useConfig();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'QuoteLineItemDataGrid.column' });
  const { t: tGroup } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'QuoteLineItemDataGrid.group' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      columnVisibilityModel: {
        [QuoteLineItemGridField.ORGANIZATION]: features.showOrganization,
        [QuoteLineItemGridField.DELIVERY_LOCATION]: features.showDeliveryLocation,
        [QuoteLineItemGridField.STATUS]: features.enableNegotiationQuoteLineItemBidding,
      },
      columns: [
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        createColumn({
          field: QuoteLineItemGridField.POSITION,
          minWidth: 70,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.TITLE,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.UNIT,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.QUANTITY,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.DECIMAL }),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ isMasked: row.isMasked, value: row.pricePerUnit })),
        }),
        createColumn({
          field: QuoteLineItemGridField.COMMODITY,
          minWidth: 200,
          renderCell: cellFactory(CommodityLinkCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            needs: {
              permission: Permission.BaseCommodityList,
            },
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.ORGANIZATION,
          minWidth: 300,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.DELIVERY_LOCATION,
          minWidth: 350,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityLocationCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.FROM,
          valueGetter: (_value, row) => row.shipmentTerms?.earlyDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
        createColumn({
          field: QuoteLineItemGridField.TO,
          valueGetter: (_value, row) => row.shipmentTerms?.lateDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
        createColumn({
          field: QuoteLineItemGridField.STATUS,
          minWidth: 150,
          editable: true,
          type: 'boolean',
          renderCell: cellFactory(NegotiationCaseQuoteStatusCell, () => ({
            viewOnly: true,
          })),
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.QUOTE,
          children: [
            //
            { field: QuoteLineItemGridField.POSITION },
            { field: QuoteLineItemGridField.TITLE },
            { field: QuoteLineItemGridField.UNIT },
            { field: QuoteLineItemGridField.QUANTITY },
            { field: QuoteLineItemGridField.PRICE_PER_UNIT },
            { field: QuoteLineItemGridField.ORGANIZATION },
            { field: QuoteLineItemGridField.DELIVERY_LOCATION },
            { field: QuoteLineItemGridField.COMMODITY },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.deliveryDate),
          children: [
            //
            { field: QuoteLineItemGridField.FROM },
            { field: QuoteLineItemGridField.TO },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.ACTIONS,
          headerAlign: GridAlignmentType.CENTER,
          children: [{ field: QuoteLineItemGridField.STATUS }],
        }),
      ],
    } as DataGridConfig<QuoteLineItemGridRow>;
  }, [
    t,
    tColumn,
    tGroup,
    features.showOrganization,
    features.showDeliveryLocation,
    features.enableNegotiationQuoteLineItemBidding,
  ]);
};
